import { assertNotEmpty } from '@festina-life/core'
import type { RawLazyTextInputProps } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback } from 'react'

import { useLazyIsAllowedToViewEntity } from '../../../hooks/annotation/useLazyIsAllowedToViewEntity'
import { useAvailableComponents } from '../../../hooks/useAvailableComponents'
import { useHook, useService } from '../../../lenteHooks'
import { useOptions } from './useOptions'

export const useLazyResolveUserInput = () => {
  const translate = useTranslate()
  const formatFullName = useService('formatter.personEntityNameFormatter')

  const { isUsingLegalEntity } = useAvailableComponents()
  const { isOption } = useOptions()
  const isAllowedToViewEntity = useLazyIsAllowedToViewEntity()

  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { mutateAsync: fetchInternalId } = useHook(
    'lazyFetchers.crm.useLazyFetchInternalId',
  )()
  const { mutateAsync: fetchPersonVersion } = useHook(
    'lazyFetchers.crm.useLazyFetchPersonVersionLatest',
  )()

  const cleanSsn = useService('logic.legalEntity.cleanSsn')
  const { validateSsn, minLength } = useService(
    'logic.legalEntity.validateSsn',
  )()

  return useCallback<RawLazyTextInputProps['resolveUserInput']>(
    async ({ userInput: dirtyUserInput, option }) => {
      if (!isOption(option)) {
        throw new Error(translate('validation.ssn'))
      }

      const userInput = dirtyUserInput.trim()
      let formValue
      switch (option) {
        case 'external': {
          const cleanUserInput = cleanSsn(userInput)
          if (!validateSsn(cleanUserInput)) {
            throw new Error(
              translate('validation.length', { length: minLength }),
            )
          }
          formValue = await fetchInternalId({
            tenantId,
            externalId: cleanUserInput,
          })
          break
        }
        case 'pseudoExternal':
          formValue = await fetchInternalId({ tenantId, externalId: userInput })
          break
        case 'internal':
          formValue = userInput
          break
      }
      assertNotEmpty(formValue, translate('validation.ssn'))

      if (
        !(await isAllowedToViewEntity({
          overwriteInternalId: formValue,
          businessType: 'person',
        }))
      ) {
        return { formValue, display: formValue }
      }

      const personVersion = isUsingLegalEntity
        ? await fetchPersonVersion({ tenantId, personId: formValue })
        : null

      return {
        formValue,
        display: personVersion
          ? formatFullName(personVersion.personEntityNameVersion)
          : '',
      }
    },
    [
      minLength,
      tenantId,
      cleanSsn,
      validateSsn,
      fetchPersonVersion,
      formatFullName,
      isOption,
      isUsingLegalEntity,
      isAllowedToViewEntity,
      translate,
      fetchInternalId,
    ],
  )
}
